import { getType, matchGuideItemNumber } from '@tunein/web-common';
import { buildContentTargeting } from 'src/client/displayAds/buildContentTargeting';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import { isCategory } from 'src/common/utils/guideItemTypes';

/**
 * A route identifier is not always a guide id. Root browse paths carry their slug (`music`,
 * `sports`), as does the now-playing dialog (`nowPlaying`), and only a type-prefixed id means
 * anything as a content source correlator or in a `/desc/` URL — `scor` is the id with its prefix
 * sliced off, so a slug would go out mangled to `usic`. Anything else contributes no guide id
 * rather than a wrong one; it can still contribute key-values.
 */
function toGuideId(identifier?: string) {
  return identifier && getType(identifier) && matchGuideItemNumber(identifier)
    ? identifier
    : undefined;
}

/**
 * What the page an outstream slot is on is about, read on every outstream ad request.
 *
 * The outstream loop is not tied to a tune: it starts on page load and refreshes on its own timer,
 * while the player's now-playing metadata only arrives once the user plays something. Until then
 * the request describes nothing — no `stationId`/`class`/`genre_id` for contextual line items to
 * match on, and a request context URL pointing at the generic home page — and most page views never
 * tune. This describes the guide item the page is about instead, the same fallback the legacy ad
 * path and the display-ads library already apply.
 *
 * Read rather than pushed because the outstream player requests its first ad as soon as the app
 * session data is ready, which is before a route's own effects have run; a push would miss it.
 *
 * The tuned session outranks all of this, so browsing station B while station A plays still
 * describes A.
 */
export function getOutstreamPageContext() {
  const state = storeSingleton.state;
  const routeGuideId = stores.appStore.getState().routeProps?.guideContext.guideId;

  if (!routeGuideId) {
    return {};
  }

  if (isCategory(routeGuideId)) {
    // Keyed the same way the category route pushes it into the player's app session data, which
    // lands only after the outstream loop's first request has already gone out.
    return { guideId: toGuideId(routeGuideId), targeting: { categoryId: routeGuideId } };
  }

  const profile = state.profiles?.[routeGuideId];
  // The profile's own id is authoritative; the route's stands in until the fetch resolves, which a
  // cold load's first outstream request can beat.
  const guideId = profile?.guideId || toGuideId(routeGuideId);

  return {
    guideId,
    targeting: buildContentTargeting(guideId ? { ...profile, guideId } : profile),
  };
}
